import {
  usePagination,
  useTracks,
  useVisualStableUpdate,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ParticipantTile } from '@/components/call/ParticipantTile';
import { Button } from '@/components/ui/button';
import type { FullscreenControl } from '@/hooks/useFullscreen';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

/**
 * The control bar floats over the stage, so the stage keeps a strip clear for
 * it. Without this the bar sits on top of the bottom row's name plates, and on
 * a phone — where the bar is widest relative to the tile — it hides the name of
 * the person you are talking to.
 *
 * On a short viewport (a phone in landscape) the reserve shrinks with the bar,
 * because there the strip would otherwise be a third of the usable height.
 */
function stagePadding(compact: boolean): string {
  // The compact reserve has to clear the bar's full footprint, not just its
  // height: at 60px the bar cut the name plates off the filmstrip tiles that
  // now sit beneath a screen share.
  return compact ? 'p-1.5 pb-18' : 'p-2 pb-20 sm:p-3 sm:pb-22';
}

/** Stable identity for a track reference, used for keys and pinning. */
function keyOf(ref: TrackReferenceOrPlaceholder): string {
  return `${ref.participant.identity}:${ref.source}`;
}

/**
 * How many tiles fit before we start paginating. Phones get fewer because a
 * ninth tile on a 5" screen is a postage stamp nobody can read a face in.
 */
function tilesPerPage(narrow: boolean): number {
  return narrow ? 4 : 9;
}

function columnsFor(count: number, narrow: boolean): number {
  if (count <= 1) return 1;
  if (narrow) return count <= 2 ? 1 : 2;
  if (count <= 2) return 2;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  return 4;
}

/**
 * The stage.
 *
 * Two layouts, chosen automatically: an even grid for a normal conversation,
 * and a focus view the moment something deserves the whole screen — a screen
 * share, or a participant someone pinned. An interview is usually two people,
 * so the grid is tuned to make two tiles look deliberate rather than sparse.
 */
export function VideoGrid({
  compactControls = false,
  fullscreen,
}: {
  compactControls?: boolean;
  fullscreen?: FullscreenControl;
}) {
  const narrow = useMediaQuery('(max-width: 860px)');

  // No `updateOnlyOn`: the default event set is what keeps the stage in step
  // with people joining, leaving and publishing. Narrowing it leaves a
  // participant who joined mid-call missing from the grid entirely.
  const trackRefs = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);

  const [pinnedKey, setPinnedKey] = useState<string | null>(null);

  const screenShare = useMemo(
    () => trackRefs.find((ref) => ref.source === Track.Source.ScreenShare),
    [trackRefs],
  );

  // A screen share outranks a manual pin: someone just chose to show something
  // to the room, and that is a stronger signal than a pin set minutes ago.
  const focused = useMemo(() => {
    if (screenShare) return screenShare;
    return trackRefs.find((ref) => keyOf(ref) === pinnedKey);
  }, [screenShare, trackRefs, pinnedKey]);

  // Drop a pin that points at someone who has left, otherwise the stage stays
  // stuck on an empty focus slot.
  useEffect(() => {
    if (pinnedKey && !trackRefs.some((ref) => keyOf(ref) === pinnedKey)) setPinnedKey(null);
  }, [pinnedKey, trackRefs]);

  const padding = stagePadding(compactControls);

  if (focused) {
    const others = trackRefs.filter((ref) => keyOf(ref) !== keyOf(focused));
    const sharing = Boolean(screenShare);

    return (
      <div
        className={cn(
          'lk-focus-layout-wrapper flex size-full min-h-0 flex-col gap-2 sm:gap-3',
          padding,
        )}
      >
        {/* Centres the focused tile, which no longer fills the stage once it
            is matched to the shared window's aspect ratio. */}
        <div className="relative flex min-h-0 flex-1 items-center justify-center">
          <ParticipantTile
            trackRef={focused}
            focused
            canFocus={!sharing}
            onToggleFocus={() => setPinnedKey(null)}
            bare={sharing}
            fullscreen={fullscreen}
          />
        </div>

        {/* The faces stay in a strip under the share, at every size. Floating
            them over a corner reclaimed a little height on a landscape phone,
            but it covered part of what was being shared and put the people you
            are talking to somewhere different depending on the device. */}
        {others.length > 0 && (
          <div className="lk-focus-filmstrip flex shrink-0 justify-center gap-2 overflow-x-auto pb-0.5">
            {others.map((ref) => (
              <div
                key={keyOf(ref)}
                className={cn('aspect-video shrink-0', compactControls ? 'h-14' : 'h-24 sm:h-28')}
                // The filmstrip is a peripheral view; pinning from here would
                // fight the screen share that put us in this layout.
              >
                <ParticipantTile
                  trackRef={ref}
                  canFocus={!sharing}
                  onToggleFocus={() => setPinnedKey(keyOf(ref))}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <PaginatedGrid trackRefs={trackRefs} narrow={narrow} padding={padding} onPin={setPinnedKey} />
  );
}

function PaginatedGrid({
  trackRefs,
  narrow,
  padding,
  onPin,
}: {
  trackRefs: TrackReferenceOrPlaceholder[];
  narrow: boolean;
  padding: string;
  onPin: (key: string) => void;
}) {
  const perPage = tilesPerPage(narrow);
  // Pulls whoever is speaking onto the first page before slicing it, so the
  // person talking is never hidden behind a pagination arrow.
  const stable = useVisualStableUpdate(trackRefs, perPage);
  const pagination = usePagination(perPage, stable);

  const count = pagination.tracks.length;
  const columns = columnsFor(count, narrow);
  const rows = Math.ceil(count / columns);
  const paged = pagination.totalPageCount > 1;

  return (
    <div className="lk-grid-layout-wrapper relative flex size-full min-h-0 flex-col">
      <div
        className={cn('lk-grid-layout grid min-h-0 flex-1 gap-2 sm:gap-3', padding)}
        style={{
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }}
      >
        {pagination.tracks.map((ref) => (
          <ParticipantTile
            key={keyOf(ref)}
            trackRef={ref}
            canFocus={count > 1}
            onToggleFocus={() => onPin(keyOf(ref))}
          />
        ))}
      </div>

      {paged && (
        // Sits above the strip the padding reserves for the control bar, which
        // is centred on the same axis and would otherwise cover these arrows.
        <div className="pointer-events-none absolute inset-x-0 bottom-19 flex items-center justify-center sm:bottom-21">
          <div className="glass pointer-events-auto flex items-center gap-1 rounded-full border p-1">
            <Button
              variant="ghost"
              size="icon-sm"
              className="rounded-full"
              onClick={pagination.prevPage}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </Button>
            <span className={cn('lk-pagination-count text-muted-foreground px-1 text-xs tabular-nums')}>
              {pagination.currentPage} / {pagination.totalPageCount}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              className="rounded-full"
              onClick={pagination.nextPage}
              aria-label="Next page"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
